import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  useTheme, 
  Divider,
  Alert,
  Container 
} from '@mui/material';
import { Calculate } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { decodeMeetingData, formatSharedDate } from '../../utils/shareUtils';
import { formatGBP } from '../../utils/formatGBP';
import { rainbowShareButtonStyles } from '../../theme/shareButtonStyles';

export default function SharedDocumentPage() {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [documentData, setDocumentData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const encodedData = searchParams.get('data');
    
    if (!encodedData) {
      setError(true);
      return;
    }

    const decoded = decodeMeetingData(encodedData);
    if (!decoded || decoded.dataType !== 'document') {
      setError(true);
      return;
    }

    setDocumentData(decoded);
  }, [searchParams]);

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          Invalid or corrupted document share link. The document data could not be loaded.
        </Alert>
        <Box textAlign="center">
          <Button
            component={Link}
            to="/documents"
            variant="contained"
            size="large"
            startIcon={<Calculate />}
            sx={{ 
              px: 4, 
              py: 2,
              fontSize: '1.1rem',
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
            }}
          >
            Try Document Calculator
          </Button>
        </Box>
      </Container>
    );
  }

  if (!documentData) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h6" textAlign="center">
          Loading document summary...
        </Typography>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)'
          : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        py: 2,
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Box textAlign="center" mb={3}>
          <Box 
            sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: 2, 
              mb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: '2.5rem',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
              }}
            >
              💸
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                fontWeight: 900,
                color: 'text.primary',
                fontSize: { xs: '2.5rem', sm: '3rem' },
              }}
            >
              cost.wtf
            </Typography>
          </Box>
          <Typography
            variant="subtitle1"
            sx={{
              color: 'text.secondary',
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              fontWeight: 600,
              fontSize: '1.1rem',
            }}
          >
            Document Cost Calculator
          </Typography>
        </Box>

        {/* Document Summary */}
        <Paper 
          elevation={6} 
          sx={{ 
            p: 3, 
            textAlign: 'center',
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            border: theme.palette.mode === 'dark' 
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid rgba(0,0,0,0.05)',
            mb: 3,
          }}
        >
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              mb: 3,
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              fontWeight: 800,
              color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
            }}
          >
            {documentData.documentTitle ? `📄 ${documentData.documentTitle}` : '📄 Document Cost Summary'}
          </Typography>
          
          {/* Total Cost Display */}
          <Box 
            sx={{ 
              mb: 3,
              p: 2,
              borderRadius: 4,
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)'
                : 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.03) 100%)',
              border: theme.palette.mode === 'dark' 
                ? '2px solid rgba(255,255,255,0.1)'
                : '2px solid rgba(25, 118, 210, 0.12)',
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                mb: 3,
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 1,
                textAlign: 'center',
              }}
            >
              📊 Document Cost Results
            </Typography>
            
            <Typography 
              variant="h1" 
              sx={{ 
                color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
                mb: 0.5,
                fontFamily: 'monospace',
                fontWeight: 'bold',
                fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                letterSpacing: 1,
              }}
            >
              {formatGBP(documentData.finalCost)}
            </Typography>
            
            {/* Cost Breakdown */}
            <Grid container spacing={2} sx={{ mt: 2, justifyContent: 'center' }}>
              <Grid size={{ xs: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                      fontWeight: 600,
                      display: 'block'
                    }}
                  >
                    Writing
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'primary.main',
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                    }}
                  >
                    {formatGBP(documentData.breakdown.writingCost)}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                      fontWeight: 600,
                      display: 'block'
                    }}
                  >
                    Review
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'secondary.main',
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                    }}
                  >
                    {formatGBP(documentData.breakdown.reviewCost)}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                      fontWeight: 600,
                      display: 'block'
                    }}
                  >
                    Reading
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'success.main',
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                    }}
                  >
                    {formatGBP(documentData.breakdown.readingCost)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ mb: 3, opacity: 0.3 }} />

          {/* Document Details */}
          <Box 
            sx={{ 
              mb: 2,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Box 
              sx={{ 
                p: 2, 
                borderRadius: 3,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.03)'
                  : 'action.hover',
                border: theme.palette.mode === 'dark' 
                  ? '1px solid rgba(255,255,255,0.05)'
                  : '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'text.secondary',
                  mb: 1,
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                  fontWeight: 600,
                }}
              >
                📝 Document Type
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
                  textTransform: 'capitalize',
                }}
              >
                {documentData.documentType}
              </Typography>
            </Box>

            <Box 
              sx={{ 
                p: 2, 
                borderRadius: 3,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.03)'
                  : 'action.hover',
                border: theme.palette.mode === 'dark' 
                  ? '1px solid rgba(255,255,255,0.05)'
                  : '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'text.secondary',
                  mb: 1,
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                  fontWeight: 600,
                }}
              >
                👥 Total People
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
                }}
              >
                {documentData.totalParticipants} {documentData.totalParticipants === 1 ? 'person' : 'people'}
              </Typography>
            </Box>
          </Box>

          {/* Shared Date */}
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              fontStyle: 'italic',
            }}
          >
            Shared on {formatSharedDate(documentData.sharedAt)}
          </Typography>
        </Paper>

        {/* Call to Action */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 2.5, 
            textAlign: 'center',
            background: 'linear-gradient(45deg, #51cf66 30%, #69db7c 90%)',
            color: 'white',
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              fontWeight: 700,
              mb: 1.5,
            }}
          >
            Calculate Your Own Document Costs
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              opacity: 0.9,
            }}
          >
            Estimate the cost of creating documentation with your team
          </Typography>
          <Button
            component={Link}
            to="/documents"
            variant="contained"
            size="large"
            startIcon={<Calculate />}
            sx={rainbowShareButtonStyles}
          >
            Try Document Calculator
          </Button>
        </Paper>

        {/* Footer */}
        <Box textAlign="center" mt={3}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
            }}
          >
            Made with ❤️ for better documentation
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}